// src/pages/quality/charts/AssessmentChartsPage.jsx
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import api from '../../../services/api';
import AssessmentsCountByTypeChart from '../../../components/quality/charts/AssessmentsCountByTypeChart';
import AssessmentsCountByReceiptChannelChart from '../../../components/quality/charts/AssessmentsCountByReceiptChannelChart';
import AssessmentsCountByOperatorChart from '../../../components/quality/charts/AssessmentsCountByOperatorChart';
import AssessmentsByOperatorsChart from '../../../components/quality/charts/AssessmentsByOperatorsChart';
import AssessmentsByCriteriaChart from '../../../components/quality/charts/AssessmentsByCriteriaChart';
import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo
} from '../../../hooks/useDateTimeFilter';
import useAuth from "../../../hooks/useAuth";

const AssessmentChartsPage = () => {
  const { projects } = useAuth();
  const [selectedProject, setSelectedProject] = useState(null);
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [fieldOptions, setFieldOptions] = useState({});
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [periodType, setPeriodType] = useState('week');
  const [ticketsCount, setTicketsCount] = useState([]);
  const [missingDates, setMissingDates] = useState([]);

  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const projectId = pathParts[pathParts.length - 1];
  const project = projects.find(p => p.id === parseInt(projectId));

  // Добавляем фильтры даты
  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;
  useEffect(() => {
    // Находим проект по ID из последнего сегмента URL
    const project = projects.find(p => p.id === parseInt(projectId));
    setSelectedProject(project);
  }, [projects, projectId]);

  // Устанавливаем дефолтные значения дат: 30 дней назад и завтра
  useEffect(() => {
    const fetchAllData = async () => {
      if (!selectedProject) return;

      try {
        setLoading(true);

        const dateFrom = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
        const dateTo = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';

        // Форматируем даты для фильтрации тикетов, проверяя их валидность
        const dateFromFormatted = dateFilters.date_filter_from && dayjs(dateFilters.date_filter_from).isValid() ? dayjs(dateFilters.date_filter_from).format('YYYY-MM-DD') : '';
        const dateToFormatted = dateFilters.date_filter_to && dayjs(dateFilters.date_filter_to).isValid() ? dayjs(dateFilters.date_filter_to).format('YYYY-MM-DD') : '';
        
        // Извлекаем month и year из selectedMonthYear
        const monthYearParts = selectedMonthYear ? selectedMonthYear.split('-') : [];
        const month = monthYearParts[0] || '';
        const year = monthYearParts[1] || '';
        
        // Загружаем оценки для выбранного проекта
        const assessmentsResponse = await api.get(`/quality/assessments`, {
          params: {
            project_id: selectedProject.id,
            date_filter_from: dateFrom,
            date_filter_to: dateTo,
            month: month,
            year: year
          }
        });
        setAssessments(assessmentsResponse.data.data || []);

        // Загружаем все опции полей для проекта
        const optionsResponse = await api.get(`/field-options/all?project_id=${selectedProject.id}`);
        // Загружаем операторов
        const operatorsResponse = await api.get(`/operators?project_id=${selectedProject.id}`);
        let operatorsList = operatorsResponse.data.operators || [];
        
        // Проверяем, есть ли в assessments agent_name, которых нет в operatorsList
        const uniqueAgentNames = [...new Set(assessments.map(a => a.agent_name).filter(Boolean))];
        
        uniqueAgentNames.forEach(agentName => {
          // Проверяем, есть ли уже такой оператор в списке
          const exists = operatorsList.some(op => 
            `${op.surname} ${op.name}` === agentName
          );
          
          if (!exists) {
            // Разделяем имя на фамилию и имя
            const nameParts = agentName.trim().split(' ');
            if (nameParts.length >= 2) {
              const surname = nameParts[0];
              const name = nameParts[1];
              
              operatorsList.push({
                id: -1, // временный ID для отсутствующих в базе операторов
                surname: surname,
                name: name
              });
            }
          }
        });
        
        if (optionsResponse.data.success) {
          setFieldOptions(prev => ({
            ...optionsResponse.data.data,
            operators: operatorsList
          }));
        }

        // Загружаем все критерии проекта
        const criteriaResponse = await api.get(`/quality/criteria?project_id=${selectedProject.id}`);
        if (criteriaResponse.data.success) {
          setCriteria(criteriaResponse.data.data || []);
        }
        
        // Загружаем месяцы для выпадающего списка
        const monthsResponse = await api.get(`/quality/assessment-months`, {
          params: { project_id: selectedProject.id }
        });
        
        // Загружаем количество тикетов
        const ticketsResponse = await api.get(`/tickets-count-by-day`, {
          params: {
            project_id: selectedProject.id,
            date_from: dateFromFormatted,
            date_to: dateToFormatted,
            month: month,
            year: year
          }
        });
        
        setTicketsCount(ticketsResponse.data.records || []);
        if (monthsResponse.data.success) {
          setMonths(monthsResponse.data.months);
        }
        // После загрузки данных, определяем отсутствующие даты
        if (dateFilters.date_filter_from && dateFilters.date_filter_to && ticketsResponse.data.records.length > 0) {  // Проверяем исходные dateFrom и dateTo
          let start = dayjs(dateFilters.date_filter_from);
          let end = dayjs(dateFilters.date_filter_to);

          // Проверяем, что start и end являются валидными dayjs объектами
          if (!start.isValid()) {
            start = dayjs(); // Используем текущую дату как fallback
          }
          if (!end.isValid()) {
            end = dayjs(); // Используем текущую дату как fallback
          }

          // Убедимся, что start не позже end
          if (start.isAfter(end)) {
            [start, end] = [end, start]; // Меняем местами
          }

          // Создаем массив всех дат в диапазоне без использования isSameOrBefore
          const allDatesInRange = [];
          let currentDate = start.clone();
          const endDate = end.endOf('day'); // Устанавливаем время конца дня

          // Получаем timestamp для сравнения
          const endTimestamp = endDate.valueOf();

          while (currentDate.valueOf() <= endTimestamp) {
            allDatesInRange.push(currentDate.format('YYYY-MM-DD'));
            currentDate = currentDate.add(1, 'day');
          }

          // Получаем даты, которые есть в ответе от API
          const existingDates = ticketsResponse.data.records.map(record => record.count_date);

          // Находим отсутствующие даты
          const missing = allDatesInRange.filter(date => !existingDates.includes(date));
          setMissingDates(missing);
        }

        if (assessmentsResponse.data && assessmentsResponse.data.filters) {
          let shouldUpdate = false;

          const serverFrom = assessmentsResponse.data.filters.date_filter_from || getDefaultDateFrom();
          const serverTo = assessmentsResponse.data.filters.date_filter_to || getDefaultDateTo();

          const inputFrom = toInputFormat(serverFrom);
          const inputTo = toInputFormat(serverTo);

          // Проверяем, нужно ли обновить фильтры
          if (inputFrom && inputFrom !== dateFilters.date_filter_from) {
            handleDateBlur({target: {name: 'date_filter_from', value: inputFrom}});
            shouldUpdate = true;
          }
          if (inputTo && inputTo !== dateFilters.date_filter_to) {
            handleDateBlur({target: {name: 'date_filter_to', value: inputTo}});
            shouldUpdate = true;
          }

          // Обновляем данные, если нужно
          if (shouldUpdate) {
            setAssessments(assessmentsResponse.data.data || []);
            return;
          }

          // Сбрасываем фильтр по месяцу, если используется фильтр по датам
          if (assessmentsResponse.data.filters.date_filter_from && assessmentsResponse.data.filters.date_filter_to) {
            setSelectedMonthYear('');
          }

          // Обновляем выбранный месяц и год из фильтров
          if (assessmentsResponse.data.filters.month && assessmentsResponse.data.filters.year) {
            const monthYearValue = `${assessmentsResponse.data.filters.month}-${assessmentsResponse.data.filters.year}`;
            if (monthYearValue !== selectedMonthYear) {
              setSelectedMonthYear(monthYearValue);
            }
          }
        }
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [selectedProject, dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);

  // Функция для получения названия месяца по номеру
  const getMonthName = (monthNumber) => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    return months[monthNumber - 1] || '';
  };

  // Формируем опции для выпадающего списка
  const monthYearOptions = months.map(m => ({
    value: `${m.month}-${m.year}`,
    label: `${getMonthName(m.month)} ${m.year}`
  }));

  // Функция для скачивания блока графиков
  const handleDownloadChart = async (rowType) => {
    let chartContainer;

    // Находим нужный контейнер блока графиков
    switch(rowType) {
      case 'first-row':
        chartContainer = document.querySelector('.charts-row:nth-child(1)');
        break;
      case 'second-row':
        chartContainer = document.querySelector('.charts-row:nth-child(2)');
        break;
      case 'third-row':
        chartContainer = document.querySelector('.charts-row:nth-child(3)');
        break;
      case 'stats-block':
        chartContainer = document.querySelector('div[style*="flex; gap: 20px"]');
        break;
      default:
        return;
    }

    if (!chartContainer) return;

    try {
      // Ждем полной отрисовки диаграмм
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Получаем полные размеры контейнера
      const chartRect = chartContainer.getBoundingClientRect();
      const fullWidth = Math.ceil(chartRect.width);
      const fullHeight = Math.ceil(chartRect.height);

      // Создаем новое canvas для объединенного изображения
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = fullWidth * 2;
      combinedCanvas.height = fullHeight * 2;

      const ctx = combinedCanvas.getContext('2d');
      ctx.scale(2, 2);

      // Заполняем фон
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, fullWidth, fullHeight);

      // Получаем все canvas элементы в контейнере
      // const canvases = chartContainer.querySelectorAll('canvas');
      
      // Получаем все canvas элементы в контейнере
      const canvases = Array.from(chartContainer.querySelectorAll('canvas'));
      
      if (canvases.length >= 2) {
        // Получаем позиции левой и правой диаграмм
        const leftCanvas = canvases[0];
        const rightCanvas = canvases[1];
        
        const leftRect = leftCanvas.getBoundingClientRect();
        const rightRect = rightCanvas.getBoundingClientRect();
        
        // Вычисляем центральную линию между диаграммами
        const centerX = (leftRect.right + rightRect.left) / 2 - chartRect.left;
        
        // Рисуем вертикальную линию между диаграммами
        ctx.beginPath();
        ctx.moveTo(centerX, 0);
        ctx.lineTo(centerX, fullHeight);
        ctx.strokeStyle = '#ddd';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      
      // Для каждого canvas элемента копируем его содержимое
      canvases.forEach(canvas => {
        const rect = canvas.getBoundingClientRect();
        const x = rect.left - chartRect.left;
        const y = rect.top - chartRect.top;
        
        // Создаем временный canvas для корректного копирования
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');
        
        // Копируем содержимое оригинального canvas в временный
        tempCtx.drawImage(canvas, 0, 0);
        
        // Копируем из временного canvas в итоговый
        ctx.drawImage(tempCanvas, x, y, canvas.width, canvas.height);
      });
      
      // Получаем все текстовые элементы и заголовки
      const headers = chartContainer.querySelectorAll('h3, .bar-label, .x-axis-label, .no-data');
      headers.forEach(header => {
        const rect = header.getBoundingClientRect();
        const x = rect.left - chartRect.left;
        const y = rect.top - chartRect.top;
        const style = window.getComputedStyle(header);
        
        ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        ctx.fillStyle = style.color;
        ctx.textAlign = style.textAlign || 'left';
        ctx.fillText(header.textContent, x, y + parseInt(style.fontSize));
      });
      
      // Создаем ссылку для скачивания
      const link = document.createElement('a');
      link.download = `блок_графиков_${rowType}_${new Date().toISOString().slice(0,10)}.png`;
      link.href = combinedCanvas.toDataURL('image/png');
      link.click();

    } catch (error) {
      console.error('Ошибка при создании изображения:', error);
      alert('Не удалось создать изображение. Попробуйте еще раз.');
    }
  };
  
  if (loading) return <div className="p-4">Загрузка данных для графиков...</div>;
  if (error) return <div className="p-4 text-danger">{error}</div>;

  return (
      <div className="main-content">
        <h1>Графики аналитики по оценкам</h1>
        {loading ? (
            <div>Загрузка...</div>
        ) : error ? (
            <div className="error">{error}</div>
        ) : (
            <div>
              <p>Проект: {selectedProject ? selectedProject.name_text : 'Не выбран'}</p>
              <p>Загружено оценок: {assessments.length}</p>
              
              {/* Фильтры даты */}
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <label className="form-label">Дата от (с секундами)</label>
                  <input
                      type="datetime-local"
                      name="date_filter_from"
                      className="form-control form-control-sm"
                      value={tempDateValues.date_filter_from || dateFilters.date_filter_from}
                      onChange={handleDateChange}
                      onBlur={handleDateBlur}
                      step="1"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Дата до (с секундами)</label>
                  <input
                      type="datetime-local"
                      name="date_filter_to"
                      className="form-control form-control-sm"
                      value={tempDateValues.date_filter_to || dateFilters.date_filter_to}
                      onChange={handleDateChange}
                      onBlur={handleDateBlur}
                      step="1"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Фильтр по месяцу</label>
                  <select
                      className="form-control form-control-sm"
                      value={selectedMonthYear}
                      onChange={(e) => setSelectedMonthYear(e.target.value)}
                  >
                    <option value="">Все месяцы</option>
                    {monthYearOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label">Период</label>
                <select
                  className="form-control form-control-sm"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                >
                  <option value="month">Месяц</option>
                  <option value="week">Неделя</option>
                </select>
              </div>

              {/* Блоки статистики */}
              <div style={{display: 'flex', gap: '20px', marginBottom: '20px'}}>
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  minWidth: '200px',
                  textAlign: 'center'
                }}>
                  <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>Всего обращений</h4>
                  <div style={{fontSize: '24px', fontWeight: 'bold', color: '#007bff'}}>
                    {ticketsCount.reduce((sum, item) => sum + item.count, 0)}
                  </div>
                  {missingDates.length > 0 && (
                    <div 
                      style={{fontSize: '12px', color: '#dc3545', marginTop: '5px', cursor: 'help'}}
                      title={`Нет данных за: ${missingDates.join(', ')}`}
                    >
                      Заполнены не все дни периода
                    </div>
                  )}
                </div>

                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  minWidth: '200px',
                  textAlign: 'center'
                }}>
                  <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>Проверено обращений</h4>
                  <div style={{fontSize: '24px', fontWeight: 'bold', color: '#28a745'}}>
                    {assessments.length}
                  </div>
                </div>

                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  minWidth: '200px',
                  textAlign: 'center'
                }}>
                  <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>% проверенных обращений к общему количеству</h4>
                  <div style={{fontSize: '24px', fontWeight: 'bold', color: '#ffc107'}}>
                    {ticketsCount.reduce((sum, item) => sum + item.count, 0) > 0
                      ? ((assessments.length / ticketsCount.reduce((sum, item) => sum + item.count, 0)) * 100).toFixed(2) + '%'
                      : '0%'
                    }
                  </div>
                </div>
                
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  minWidth: '200px',
                  textAlign: 'center'
                }}>
                  <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>Результаты проверок</h4>
                  <div style={{fontSize: '24px', fontWeight: 'bold', color: '#28a745'}}>
                    {assessments.length > 0
                      ? (assessments.reduce((sum, a) => sum + (a.total_score || 0), 0) / assessments.length * 100).toFixed(2) + '%'
                      : '0%'
                    }
                  </div>
                </div>
                
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  minWidth: '200px',
                  textAlign: 'center'
                }}>
                  <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>Количество критов</h4>
                  <div style={{fontSize: '24px', fontWeight: 'bold', color: '#dc3545'}}>
                    {assessments.reduce((count, assessment) => {
                      return count + assessment.items.filter(item => item.critical && item.score === 0).length;
                    }, 0)}
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-end mb-3">
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleDownloadChart('stats-block')}
                >
                  Скачать блок статистики
                </button>
              </div>

              <div className="charts-container">
                <div className="charts-row">
                  <div className="chart-card w-50">
                    <AssessmentsCountByReceiptChannelChart
                        assessments={assessments}
                        fieldOptions={fieldOptions}
                    />
                  </div>
                  <div className="chart-card w-50">
                    <AssessmentsCountByTypeChart
                        assessments={assessments}
                        fieldOptions={fieldOptions}
                    />
                  </div>


                  <div className="d-flex justify-content-end mt-2">
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadChart('first-row')}
                    >
                      Скачать блок графиков
                    </button>
                  </div>
                </div>

                <div className="charts-row">
                  <div className="chart-card w-50">
                    <AssessmentsCountByOperatorChart
                        assessments={assessments}
                        fieldOptions={fieldOptions}
                    />
                  </div>
                  <div className="chart-card w-50">
                    <AssessmentsByOperatorsChart
                        assessments={assessments}
                        fieldOptions={fieldOptions}
                    />
                  </div>
                  <div className="d-flex justify-content-end mt-2">
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadChart('second-row')}
                    >
                      Скачать блок графиков
                    </button>
                  </div>
                </div>
                <div className="charts-row">
                  <div className="chart-card w-50">
                    <AssessmentsByCriteriaChart
                        assessments={assessments}
                        criteria={criteria}
                        fieldOptions={fieldOptions}
                    />
                  </div>
                  <div className="d-flex justify-content-end mt-2">
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDownloadChart('third-row')}
                    >
                      Скачать блок графиков
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}
      </div>
  )
};

export default AssessmentChartsPage;